import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DetailsHeader } from "./detailHeader";
import { KeyStats } from "./keyStats";
import { Button } from "@shared/ui/button";
import { Pencil, FileText, Plus, ArrowLeft } from "lucide-react";
import { Tabs } from "@shared/ui/tabs";

import { MetersTab } from "./tabs/meters/landing";
import { useGetRouteData } from "../../hooks";
import { UpdateReadCycleModal } from "./updateReadCycleModal";
import { PerformanceTabLanding } from "./tabs/performance/landing";
import { AddPremiseModal } from "./addPremiseModal";
import { PremisesTab } from "./tabs/premise/premiseList";

import { PageHeader } from "@shared/components/PageHeader";
import { NotesTab } from "@features/mx/shared/components/NotesTab";
import { ActivityLogTab } from "@features/mx/shared/components/ActivityLog";

const RouteDetails = () => {
  const { route_id } = useParams();
  const navigate = useNavigate();
  const { data: routeDetailsData, refetch: refetchRouteDetails } =
    useGetRouteData(route_id);
  const [isUpdateReadCycleOpen, setIsUpdateReadCycleOpen] = useState(false); // ✅ Modal state
  const [isAddPremiseOpen, setIsAddPremiseOpen] = useState(false); // ✅ new state
  const source = "0";
  const handleMoveRoutes = (destinationCycleId: string) => {};

  const tabComponents = {
    performance: {
      label: "Performance",
      shortLabel: "performance",
      component: <PerformanceTabLanding />,
    },
    premises: {
      label: "Premises",
      shortLabel: "premises",
      component: <PremisesTab routeDetails={routeDetailsData?.[0]} />,
    },
    meters: {
      label: "Meters",
      shortLabel: "meters",
      component: <MetersTab />,
    },
    notes: {
      label: "Notes",
      shortLabel: "notes",
      component: <NotesTab requestId={route_id} source={source} />,
    },
    logs: {
      label: "Activity Log",
      shortLabel: "logs",
      component: <ActivityLogTab entityId={route_id} entityType={source} />,
    },
  };

  const urlMapping = {
    performance: "performance",
    premises: "premises",
    meters: "meters",
    logs: "logs",
    notes: "notes",
  };

  // ✅ Moved QuickActions here so it can access modal open handler
  const QuickActions = () => {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <h3 className="text-lg font-semibold text-gray-800 mb-1">
          Quick Actions
        </h3>
        <p className="text-sm text-gray-500 mb-6">
          Manage this route using common actions.
        </p>

        <div className="space-y-2 grid grid-cols-1">
          <Button
            variant="outline"
            onClick={() => navigate(`/mx/routeManagement/edit/${route_id}`)}
          >
            <Pencil className="w-4 h-4 mr-2" />
            Edit Route
          </Button>

          {/* ✅ Open modal on click */}
          <Button
            variant="outline"
            onClick={() => setIsUpdateReadCycleOpen(true)}
          >
            <FileText className="w-4 h-4" />
            Update Read Cycle
          </Button>

          <Button variant="outline" onClick={() => setIsAddPremiseOpen(true)}>
            <Plus className="w-4 h-4" />
            Add Premises
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="mb-4 flex items-center gap-4">
        {/* Back Arrow */}
        <ArrowLeft
          className="w-5 h-5 cursor-pointer text-gray-700"
          onClick={() => navigate("/mx/routeManagement/")}
        />

        {/* Page Header */}
        <div className="flex flex-col">
          <PageHeader
            title="View Route"
            subtitle="Track performance and progress of your meter reading route"
          />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-6">
        {/* Left Column - Details */}
        <div className="col-span-2">
          <DetailsHeader routeDetails={routeDetailsData} />
        </div>

        {/* Right Column - Key Stats + Quick Actions */}
        <div className="col-span-1">
          <KeyStats />
          <QuickActions />
        </div>
      </div>

      <div className="pt-4">
        <Tabs
          defaultValue="performance"
          urlMapping={urlMapping}
          tabComponents={tabComponents}
          tabsListFullWidth={true}
          tabsListClassName="grid grid-cols-5"
        />
      </div>

      {/* ✅ Include the dialog */}
      <UpdateReadCycleModal
        open={isUpdateReadCycleOpen}
        onOpenChange={setIsUpdateReadCycleOpen}
        selectedRoutes={[route_id || ""]}
        onMoveRoutes={handleMoveRoutes}
        estimatedMeters={50} // Example count, replace dynamically if needed
      />
      <AddPremiseModal
        routeDetails={routeDetailsData?.[0]}
        open={isAddPremiseOpen}
        onOpenChange={setIsAddPremiseOpen}
        onSuccess={refetchRouteDetails}
      />
    </div>
  );
};

export default RouteDetails;
